import React, { useEffect, useState } from "react";
import { getJiraFields } from "../api/AppLinkApi";
import RuleFieldMapper from "./component/RuleFieldMapper";
import { getRule } from "../api/RuleApi";
import { RuleForm } from "./component/RuleForm";
import Tab from "../main/component/Tab";
import { useTranslation } from "react-i18next";

const JiraSettingTabs = (t) => ({
  SETTING: {
    key: "SETTING",
    value: <span className={"px-1.5"}>{t("Settings")}</span>,
  },
  WORKFLOW: {
    key: "WORKFLOW",
    value: <span className={"px-1.5"}>{t("Workflow")}</span>,
  },
});

const JiraSetting = ({ appInfo, onChange, ruleId, connectionStatus }) => {
  const { t } = useTranslation();
  const [jiraFields, setJiraFields] = useState([]);
  const [mappings, setMappings] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("SETTING");
  const supportedTypes = ["number", "string", "datetime"];

  useEffect(() => {
    getJiraFields().then((results) => {
      const fields = [];
      results.forEach((field) => {
        const { schema = {}, id, name }: any = field;
        if (supportedTypes.includes(schema.type)) {
          fields.push({ value: id, label: name });
        } else {
          fields.push(...getNestedTypeFields(field));
        }
      });

      setJiraFields(fields);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getNestedTypeFields = (field) => {
    const userFields = ["creator", "reporter"];
    const userProps = [
      { value: "accountId", label: t("Account Id") },
      { value: "avatarUrls.48x48", label: t("Avatar Url") },
      { value: "displayName", label: t("Display Name") },
    ];
    const fields = [];
    userProps.forEach((userProp) => {
      if (userFields.includes(field.id)) {
        fields.push({
          value: `${field.id}.${userProp.value}`,
          label: `${field.name} ${userProp.label}`,
        });
      }
    });
    const commonFields = ["project", "status", "priority", "issuetype"];
    commonFields.forEach((commonField) => {
      if (commonField == field.id) {
        fields.push({
          value: `${field.id}.id`,
          label: `${field.name} Id`,
        });
        fields.push({
          value: `${field.id}.name`,
          label: `${field.name} Name`,
        });
      }
    });
    return fields;
  };

  useEffect(() => {
    getRule(ruleId)
      .then((data) => {
        const { actions = [] } = data;
        if (actions.length > 0) {
          setMappings(actions[0].data.mappings);
          setErrorMessage("");
        }
        if (!data.enabled) {
          setErrorMessage(t("App is disabled. Please re-enable JSM app."));
        }
      })
      .catch(() => {
        setErrorMessage(t("Something went wrong!. Please re-enable JSM app."));
      });
  }, [jiraFields]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderError = () => {
    if (errorMessage != "") {
      return <div className={"text-red-600"}>{errorMessage}</div>;
    }
  };

  const renderContent = () => {
    if (activeTab === "SETTING") {
      return (
        <>
          <RuleFieldMapper
            sourcePlaceholder={t("Jira issue field")}
            sourceOptions={{
              SALE: jiraFields,
              CONTACT: jiraFields,
              COMPANY: jiraFields,
            }}
            fieldMappings={mappings}
            onChangeMappings={(newMappings) => {
              onChange({
                app: appInfo.app,
                fieldId: "mappings",
                fieldValue: newMappings,
              });
            }}
          />
        </>
      );
    }
    return (
      <>
        <RuleForm projectId={appInfo.projectId} ruleId={ruleId} />
      </>
    );
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-4 mb-4">
        <div className="col-span-12 lg:col-span-3">
          <div className={"space-y-2 align-middle"}>
            <div className={"w-40"}>{t("Status")}</div>
            <div className={"text-sm text-gray-400"}>{t("Connection to Jira")}</div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-9">
          <div className="flex items-center gap-x-3">
            {connectionStatus()}
            {renderError()}
          </div>
        </div>
      </div>
      {!errorMessage && (
        <>
          <Tab
            items={Object.values(JiraSettingTabs(t))}
            value={activeTab}
            onTabChange={setActiveTab}
          />
          {renderContent()}
        </>
      )}
    </>
  );
};

export default JiraSetting;
